import { toIsoDate } from '@/utils/dateString';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  Save,
  User,
  Phone,
  GraduationCap,
  Award,
  Briefcase,
  Users,
  FileText,
  Upload,
  Trash2,
  Eye,
  Image,
  File,
  Keyboard,
  ChevronDown,
  Check,
  AlertTriangle,
  FolderOpen,
} from 'lucide-react';
import {
  adminGetStudentProfile,
  adminUpdateStudentProfile,
  adminUploadStudentDocument,
  adminDeleteStudentDocument,
  adminGetStudentDocuments,
  type UserProfileResponse,
  type StudentProfileData,
  type StudentDocumentType,
  type StudentDocument,
  type FiliereChangeRecord,
} from '@/services/profileService';
import {
  fetchSeriesBac,
  fetchProvinces,
  fetchDiplomes,
  fetchCountries,
  type NomenclatureSerieBac,
  type NomenclatureProvince,
  type NomenclatureDiplome,
  type NomenclatureCountry,
} from '@/services/nomenclatureService';
import { fetchFilieres, type NomenclatureFiliere } from '@/services/filieresService';
import { StudentFinancialSection } from './StudentFinancialSection';
import {
  MentionDiplomeLabels,
  ResidentStatusLabels,
  FonctionnaireStatusLabels,
  MobiliteStatusLabels,
} from '@/types/etudiant.types';
import { emergencyRelationSelectOptions } from '@/shared/constants/emergencyRelation';
import { toCountryCode } from '@/shared/constants/countryCode';
import { omitEmptyStrings } from '@/utils/omitEmptyStrings';

interface DirectEditStudentProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentUserId: string;
  studentName: string;
  onSuccess?: () => void;
}

// ==================== CONSTANTS ====================

const ARABIC_KEYBOARD_LAYOUT = [
  ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'],
  ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط'],
  ['ئ', 'ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ', 'د'],
];

const COUNTRY_PHONE_CODES = [
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: "Côte d'Ivoire", dialCode: '+225', flag: '🇨🇮' },
];

// Emergency contact relationship options
// Situation options
const SITUATION_OPTIONS = [
  { value: 'N', label: 'Nouveau inscrit' },
  { value: 'R', label: 'Redoublant' },
  { value: 'P', label: 'Promu' },
];

// Generate academic years for dropdown
function generateAnneesUniversitaires(count: number = 20): string[] {
  const currentYear = new Date().getFullYear();
  const annees: string[] = [];
  for (let year = currentYear + 1; year >= currentYear - count + 1; year--) {
    annees.push(`${year}/${year + 1}`);
  }
  return annees;
}

// Helper function to parse phone number and extract country code
function parsePhoneNumber(phone: string | undefined | null): { countryCode: string; number: string } {
  if (!phone) return { countryCode: 'MA', number: '' };

  // Check if phone starts with a known country code
  for (const country of COUNTRY_PHONE_CODES) {
    if (phone.startsWith(country.dialCode)) {
      return {
        countryCode: country.code,
        number: phone.slice(country.dialCode.length).trim(),
      };
    }
  }

  // If no country code found, assume it's just the number with MA as default
  // Also strip leading + if present
  const cleanNumber = phone.replace(/^\+/, '');
  return { countryCode: 'MA', number: cleanNumber };
}

// ==================== SUB-COMPONENTS ====================

// Virtual Arabic Keyboard
const VirtualArabicKeyboard: React.FC<{
  onKeyPress: (char: string) => void;
  onBackspace: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}> = ({ onKeyPress, onBackspace, onClose, anchorRef }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 300 });

  useEffect(() => {
    const updatePosition = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const keyboardHeight = 180;
        const viewportHeight = window.innerHeight;
        const showAbove = rect.bottom + keyboardHeight > viewportHeight && rect.top > keyboardHeight;

        setPosition({
          top: showAbove ? rect.top - keyboardHeight - 5 : rect.bottom + 5,
          left: rect.left,
          width: Math.max(rect.width, 280),
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      data-arabic-keyboard
      className="fixed z-[9999] p-2 bg-white border border-gray-300 rounded-lg shadow-xl"
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200">
        <span className="text-xs text-gray-500 font-medium">لوحة المفاتيح العربية</span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-1">
        {ARABIC_KEYBOARD_LAYOUT.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-0.5">
            {row.map((char, charIndex) => (
              <button
                key={charIndex}
                type="button"
                onClick={() => onKeyPress(char)}
                className="w-7 h-8 text-sm font-medium bg-gray-100 hover:bg-primary-100 hover:text-primary-700 rounded transition-colors border border-gray-200"
              >
                {char}
              </button>
            ))}
          </div>
        ))}
        <div className="flex justify-center gap-1 mt-2">
          <button
            type="button"
            onClick={() => onKeyPress(' ')}
            className="flex-1 h-8 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
          >
            espace
          </button>
          <button
            type="button"
            onClick={onBackspace}
            className="w-16 h-8 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200"
          >
            ← effacer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Arabic Input Component
const ArabicInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const keyboardElement = document.querySelector('[data-arabic-keyboard]');
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        (!keyboardElement || !keyboardElement.contains(e.target as Node))
      ) {
        setShowKeyboard(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyPress = (char: string) => {
    const input = inputRef.current;
    if (input) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      const newValue = value.slice(0, start) + char + value.slice(end);
      onChange(newValue);
      setTimeout(() => {
        const newPos = start + char.length;
        input.setSelectionRange(newPos, newPos);
        input.focus();
      }, 0);
    } else {
      onChange(value + char);
    }
  };

  const handleBackspace = () => {
    const input = inputRef.current;
    if (input && value.length > 0) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      if (start === end && start > 0) {
        const newValue = value.slice(0, start - 1) + value.slice(end);
        onChange(newValue);
      } else if (start !== end) {
        const newValue = value.slice(0, start) + value.slice(end);
        onChange(newValue);
      }
    } else if (value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        dir="rtl"
        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-right"
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => {
          setShowKeyboard(!showKeyboard);
          if (!showKeyboard) inputRef.current?.focus();
        }}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
          showKeyboard ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'
        }`}
        title={showKeyboard ? 'Fermer le clavier' : 'Ouvrir le clavier arabe'}
      >
        <Keyboard className="w-4 h-4" />
      </button>

      {showKeyboard && (
        <VirtualArabicKeyboard
          onKeyPress={handleKeyPress}
          onBackspace={handleBackspace}
          onClose={() => setShowKeyboard(false)}
          anchorRef={containerRef}
        />
      )}
    </div>
  );
};

// Phone Country Selector
const PhoneCountrySelector: React.FC<{
  value: string;
  onChange: (code: string) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = COUNTRY_PHONE_CODES.find((c) => c.code === value) || COUNTRY_PHONE_CODES[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-2 border border-gray-300 rounded-l-lg bg-gray-50 hover:bg-gray-100 min-w-[90px] h-[42px]"
      >
        <span className="text-lg">{selected.flag}</span>
        <span className="text-sm text-gray-600">{selected.dialCode}</span>
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {COUNTRY_PHONE_CODES.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => {
                onChange(country.code);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${
                value === country.code ? 'bg-primary-50' : ''
              }`}
            >
              <span className="text-lg">{country.flag}</span>
              <span className="flex-1 text-sm">{country.name}</span>
              <span className="text-xs text-gray-500">{country.dialCode}</span>
              {value === country.code && <Check className="w-4 h-4 text-primary-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Filterable Dropdown Component
interface FilterableDropdownOption {
  value: string;
  label: string;
  sublabel?: string;
}

const FilterableDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: FilterableDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, onChange, options, placeholder = 'Sélectionner...', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);

  const filtered = isTyping && inputValue
    ? options.filter(o =>
        o.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        (o.sublabel && o.sublabel.toLowerCase().includes(inputValue.toLowerCase()))
      )
    : options;

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
        setInputValue('');
        setIsTyping(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsTyping(true);
    if (!isOpen) setIsOpen(true);
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setInputValue('');
    setIsTyping(false);
  };

  const displayValue = isTyping ? inputValue : (selected?.label || '');

  const dropdownMenu = isOpen ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999,
      }}
    >
      {filtered.length === 0 ? (
        <div className="px-4 py-3 text-sm text-gray-500 text-center">Aucun résultat</div>
      ) : (
        filtered.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${
              value === option.value ? 'bg-primary-50' : ''
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900 truncate">{option.label}</div>
              {option.sublabel && (
                <div className="text-xs text-gray-500 truncate">{option.sublabel}</div>
              )}
            </div>
            {value === option.value && <Check className="w-4 h-4 text-primary-600 flex-shrink-0 ml-2" />}
          </button>
        ))
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`w-full flex items-center border rounded-lg transition-colors ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
        } border-gray-300 ${isOpen ? 'ring-2 ring-primary-500 border-primary-500' : ''}`}
      >
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-4 py-2 bg-transparent rounded-lg focus:outline-none ${
            disabled ? 'cursor-not-allowed text-gray-500' : 'text-gray-900'
          } ${!displayValue ? 'text-gray-500' : ''}`}
        />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="px-2 py-2"
        >
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {dropdownMenu}
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  documentName: string;
  isDeleting: boolean;
}> = ({ isOpen, onClose, onConfirm, documentName, isDeleting }) => {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Supprimer le document</h3>
            <p className="text-sm text-gray-500 mt-1">Cette action est irréversible</p>
          </div>
        </div>

        <p className="text-gray-600 mb-6">
          Êtes-vous sûr de vouloir supprimer le document <strong className="text-gray-900">"{documentName}"</strong> ?
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Suppression...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Supprimer
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Document type configuration
const documentTypes: { type: StudentDocumentType; label: string; description: string }[] = [
  { type: 'CIN_FRONT', label: 'CIN (Recto)', description: 'Face avant de la carte d\'identité' },
  { type: 'CIN_BACK', label: 'CIN (Verso)', description: 'Face arrière de la carte d\'identité' },
  { type: 'PASSPORT', label: 'Passeport', description: 'Page d\'identité du passeport' },
  { type: 'PHOTO', label: 'Photo d\'identité', description: 'Photo d\'identité récente' },
  { type: 'DIPLOME_BAC_SCAN', label: 'Diplôme du Bac', description: 'Scan du diplôme du baccalauréat' },
  { type: 'RELEVES_NOTES_BAC', label: 'Relevés de notes Bac', description: 'Relevés de notes du baccalauréat' },
  { type: 'DIPLOME_ACCES_SCAN', label: 'Diplôme d\'accès', description: 'Scan du diplôme d\'accès' },
  { type: 'RELEVES_NOTES_ACCES', label: 'Relevés diplôme accès', description: 'Relevés de notes du diplôme d\'accès' },
  { type: 'CV', label: 'CV', description: 'Curriculum Vitae' },
  { type: 'LETTRE_MOTIVATION', label: 'Lettre de motivation', description: 'Lettre de motivation' },
];

/**
 * Only offered to students who actually changed filière: E-DEP expects the
 * signed PV in their dossier, and nobody else's.
 */
const FILIERE_CHANGE_DOCUMENT: { type: StudentDocumentType; label: string; description: string } = {
  type: 'PV_CHANGEMENT_FILIERE',
  label: 'PV de changement de filière',
  description: 'PV signé autorisant le transfert vers une autre filière (E-DEP)',
};

// Physical document tracking configuration
type StudentPhysicalDocField =
  | 'physicalDocStudentCin'
  | 'physicalDocStudentBac'
  | 'physicalDocStudentReleveBac'
  | 'physicalDocStudentDernierDiplome'
  | 'physicalDocStudentReleveDiplome'
  | 'physicalDocStudentReleveAnneesPrecedentes'
  | 'physicalDocStudentCv'
  | 'physicalDocStudentLettreMotivation';

const physicalDocFields: { field: StudentPhysicalDocField; label: string; hint?: string; condition?: 'nonBac' | 'transfer' }[] = [
  { field: 'physicalDocStudentCin', label: 'CIN / Pièce d\'identité' },
  { field: 'physicalDocStudentBac', label: 'Diplôme du Bac' },
  { field: 'physicalDocStudentReleveBac', label: 'Relevé de notes du Bac' },
  { field: 'physicalDocStudentDernierDiplome', label: 'Dernier diplôme', hint: 'Requis car diplôme d\'accès ≠ Bac', condition: 'nonBac' },
  { field: 'physicalDocStudentReleveDiplome', label: 'Relevé dernier diplôme', hint: 'Requis car diplôme d\'accès ≠ Bac', condition: 'nonBac' },
  { field: 'physicalDocStudentReleveAnneesPrecedentes', label: 'Relevés années précédentes', hint: 'Requis pour les transferts (niveau initial > 1)', condition: 'transfer' },
  { field: 'physicalDocStudentCv', label: 'CV (optionnel)' },
  { field: 'physicalDocStudentLettreMotivation', label: 'Lettre de motivation (optionnel)' },
];

// Same applicability rule as StudentPhysicalDocumentsTrackingModal / Suivi Dossiers:
// a diplôme d'accès is "Bac" when empty or containing a Bac variant.
const isBacDiplomeAcces = (diplome?: string): boolean => {
  if (!diplome) return true;
  const normalized = diplome.toLowerCase().trim();
  return normalized === 'bac' ||
         normalized === 'baccalauréat' ||
         normalized === 'baccalaureat' ||
         normalized.startsWith('bac ') ||
         normalized.includes('baccalaur');
};

// Static options for select fields
const genderOptions = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Féminin' },
];

const handicapOptions = [
  { value: 'N', label: 'Aucun' },
  { value: 'A', label: 'Auditif' },
  { value: 'M', label: 'Moteur' },
  { value: 'V', label: 'Visuel' },
  { value: 'AM', label: 'Auditif et Moteur' },
  { value: 'AV', label: 'Auditif et Visuel' },
  { value: 'MV', label: 'Moteur et Visuel' },
  { value: 'T', label: 'Tous' },
  { value: 'XX', label: 'Autre' },
];

const mentionOptions = Object.entries(MentionDiplomeLabels).map(([value, label]) => ({
  value,
  label,
}));

const residentOptions = Object.entries(ResidentStatusLabels).map(([value, label]) => ({
  value,
  label,
}));

const fonctionnaireOptions = Object.entries(FonctionnaireStatusLabels).map(([value, label]) => ({
  value,
  label,
}));

const mobiliteOptions = Object.entries(MobiliteStatusLabels).map(([value, label]) => ({
  value,
  label,
}));

export const DirectEditStudentProfileModal: React.FC<DirectEditStudentProfileModalProps> = ({
  isOpen,
  onClose,
  studentUserId,
  studentName,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [emailNotice, setEmailNotice] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);

  // Form data
  const [formData, setFormData] = useState<StudentProfileData>({});

  // Documents state
  const [documents, setDocuments] = useState<StudentDocument[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState<StudentDocumentType | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<StudentDocument | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedDocType, setSelectedDocType] = useState<StudentDocumentType | null>(null);

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<StudentDocument | null>(null);

  // Phone country state
  const [phoneCountry, setPhoneCountry] = useState('MA');
  const [urgencePhoneCountry, setUrgencePhoneCountry] = useState('MA');

  // Academic fields state
  const [codeFiliereInscrit, setCodeFiliereInscrit] = useState('');
  // Filière the student was enrolled in when the modal opened. Moving away
  // from it requires an official "PV de changement de filière" (E-DEP).
  const [originalCodeFiliere, setOriginalCodeFiliere] = useState('');
  // The PV is dated the day the change is recorded unless the admin says otherwise.
  const [pvChangementDate, setPvChangementDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [pvChangementMotif, setPvChangementMotif] = useState('');
  // Filière transfers already recorded for this student (admin-only history).
  const [filiereChanges, setFiliereChanges] = useState<FiliereChangeRecord[]>([]);
  const [studentAcademicYearId, setStudentAcademicYearId] = useState<string | null>(null);
  const [niveauFormation, setNiveauFormation] = useState(1);
  const [situation, setSituation] = useState('N');
  const [premiereAnneeInscription, setPremiereAnneeInscription] = useState('');

  // Nomenclature data
  const [seriesBac, setSeriesBac] = useState<NomenclatureSerieBac[]>([]);
  const [provinces, setProvinces] = useState<NomenclatureProvince[]>([]);
  const [diplomes, setDiplomes] = useState<NomenclatureDiplome[]>([]);
  const [countries, setCountries] = useState<NomenclatureCountry[]>([]);
  const [filieres, setFilieres] = useState<NomenclatureFiliere[]>([]);

  // Generate bac years list
  const anneesUniversitaires = generateAnneesUniversitaires(20);

  // Find selected filière
  const selectedFiliere = filieres.find(f => f.codeFiliere === codeFiliereInscrit);
  const previousFiliere = filieres.find(f => f.codeFiliere === originalCodeFiliere);
  /** Parent filière of a code — an option and its mother share the same one. */
  const rootOf = (code: string): string =>
    filieres.find((f) => f.codeFiliere === code)?.parentCodeFiliere || code;

  /**
   * First year in which the options of a parent filière apply (same default as
   * the backend: first semester of the last year). An option chosen before that
   * year would hide the student from Gestion des groupes.
   */
  const optionBranchYear = (parentCode: string): number | null => {
    const parent = filieres.find((f) => f.codeFiliere === parentCode);
    if (!parent) return null;
    const start = parent.specialisationStartSemester ?? (parent.nombreAnnees - 1) * 2 + 1;
    return Math.ceil(start / 2);
  };
  const optionTooEarlyMessage = ((): string | null => {
    const f = filieres.find((x) => x.codeFiliere === codeFiliereInscrit);
    if (!f?.parentCodeFiliere) return null;
    const branch = optionBranchYear(f.parentCodeFiliere);
    if (!branch || niveauFormation >= branch) return null;
    const parent = filieres.find((x) => x.codeFiliere === f.parentCodeFiliere);
    return `« ${f.filiere} » est une option qui ne commence qu'en ${branch}ème année. Pour la ${
      niveauFormation === 1 ? '1ère' : `${niveauFormation}ème`
    } année, choisissez la filière « ${parent?.filiere ?? f.parentCodeFiliere} ».`;
  })();

  /**
   * A real change of filière. Picking or switching an option inside the same
   * cursus is not one: both codes hang off the same parent filière.
   */
  const isFiliereChange = Boolean(
    originalCodeFiliere &&
      codeFiliereInscrit &&
      codeFiliereInscrit !== originalCodeFiliere &&
      rootOf(originalCodeFiliere) !== rootOf(codeFiliereInscrit),
  );

  /** The transfer already on file for the year the student is enrolled in. */
  const currentYearChange = filiereChanges.find(
    (c) => !studentAcademicYearId || c.academicYearId === studentAcademicYearId,
  );

  /**
   * Back to the filière the student started the year in: the transfer is void.
   * Compared by cursus, so returning through any option of that filière counts.
   */
  const isReturnToOrigin = Boolean(
    isFiliereChange &&
      currentYearChange?.fromCodeFiliere &&
      rootOf(currentYearChange.fromCodeFiliere) === rootOf(codeFiliereInscrit),
  );

  /**
   * Only a student who has completed a year is transferring: promu, or
   * redoublant beyond year 1. For a nouveau — or a redoublant still in year 1 —
   * the same edit corrects the enrolment record and asks for nothing.
   */
  const hasCompletedAYear = situation === 'P' || (situation === 'R' && niveauFormation > 1);
  const requiresFiliereChangePv = isFiliereChange && hasCompletedAYear && !isReturnToOrigin;

  /** A second move in the same year rewrites the transfer already recorded. */
  const replacesExistingTransfer = Boolean(requiresFiliereChangePv && currentYearChange);
  /**
   * The PV upload slot appears for students who have already been transferred,
   * and for the transfer being recorded right now.
   */
  const visibleDocumentTypes =
    (filiereChanges.length > 0 && !isReturnToOrigin) || requiresFiliereChangePv
      ? [...documentTypes, FILIERE_CHANGE_DOCUMENT]
      : documentTypes;
  const maxNiveau = selectedFiliere?.nombreAnnees || 5;

  // Load profile and nomenclature data
  useEffect(() => {
    if (isOpen && studentUserId) {
      loadData();
    }
  }, [isOpen, studentUserId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Load profile, documents, and nomenclature data in parallel
      const [profileData, docsData, seriesData, provincesData, diplomesData, countriesData, filieresData] = await Promise.all([
        adminGetStudentProfile(studentUserId),
        adminGetStudentDocuments(studentUserId),
        fetchSeriesBac(),
        fetchProvinces(),
        fetchDiplomes(),
        fetchCountries(),
        fetchFilieres(),
      ]);

      setProfile(profileData);
      setDocuments(docsData || []);
      setSeriesBac(seriesData || []);
      setProvinces(provincesData || []);
      setDiplomes(diplomesData || []);
      setCountries(countriesData || []);
      setFilieres(filieresData || []);

      // Initialize form data from profile
      if (profileData) {
        const student = profileData.student;
        const userProfile = profileData.profile;

        // Parse phone numbers to extract country code and number separately
        const rawPhone = student?.phone || profileData.phone || '';
        const rawUrgencePhone = student?.emergencyContactPhone || userProfile?.urgenceNumero || '';

        // Use saved phoneCountry if available, otherwise parse from phone number
        const parsedPhone = parsePhoneNumber(rawPhone);
        const parsedUrgencePhone = parsePhoneNumber(rawUrgencePhone);

        // Set phone country codes (prefer saved value, fall back to parsed)
        const phoneCountryValue = userProfile?.phoneCountry || parsedPhone.countryCode;
        const urgencePhoneCountryValue = userProfile?.urgencePhoneCountry || parsedUrgencePhone.countryCode;

        setPhoneCountry(phoneCountryValue);
        setUrgencePhoneCountry(urgencePhoneCountryValue);

        // Set academic fields from student record
        setCodeFiliereInscrit(student?.codeFiliereInscrit || '');
        setOriginalCodeFiliere(student?.codeFiliereInscrit || '');
        setFiliereChanges(profileData.filiereChanges || []);
        setStudentAcademicYearId(student?.academicYearId ?? null);
        setNiveauFormation(student?.niveauFormation || 1);
        setSituation(student?.situation || 'N');
        setPremiereAnneeInscription(student?.premiereAnneeInscription || '');

        setFormData({
          // Identity
          nom: student?.lastName || profileData.lastName || '',
          prenom: student?.firstName || profileData.firstName || '',
          nomArabe: student?.lastNameArabic || userProfile?.nomArabe || '',
          prenomArabe: student?.firstNameArabic || userProfile?.prenomArabe || '',
          cnie: student?.cnie || userProfile?.cnie || '',
          passport: student?.passportNumber || userProfile?.passport || '',
          sexe: student?.gender === 'MALE' ? 'M' : student?.gender === 'FEMALE' ? 'F' : userProfile?.sexe || '',
          dateNaissance: toIsoDate(student?.dateOfBirth) || toIsoDate(userProfile?.dateNaissance),
          paysOrigine: toCountryCode(student?.nationality) || toCountryCode(userProfile?.paysOrigine),
          handicap: userProfile?.handicap || '',
          // Contact
          email: profileData.email || student?.email || '',
          telephone: parsedPhone.number,
          phoneCountry: phoneCountryValue,
          adresse: student?.address || userProfile?.adresse || '',
          // Emergency contact
          urgenceNom: student?.emergencyContactName || userProfile?.urgenceNom || '',
          urgenceRelation: student?.emergencyContactRelation || userProfile?.urgenceRelation || '',
          urgencePhoneCountry: urgencePhoneCountryValue,
          urgenceNumero: parsedUrgencePhone.number,
          urgenceAdresse: userProfile?.urgenceAdresse || '',
          // Academic
          numeroMassarCNE: student?.numeroMassarCNE || '',
          // Diploma access
          serieBaccalaureat: student?.serieBaccalaureat || '',
          anneeObtentionBac: student?.anneeObtentionBac || '',
          diplomeAcces: student?.diplomeAcces || '',
          specialiteDiplomeAcces: student?.specialiteDiplomeAcces || '',
          mentionDiplomeAcces: student?.mentionDiplomeAcces || '',
          lieuObtentionDiplomeAcces: student?.lieuObtentionDiplomeAcces || '',
          // Status & Aids
          resident: student?.resident || '',
          fonctionnaire: student?.fonctionnaire || '',
          pourcentageBourse: student?.pourcentageBourse ?? undefined,
          mobilite: student?.mobilite || '',
          // Physical document tracking
          physicalDocStudentCin: userProfile?.physicalDocStudentCin ?? false,
          physicalDocStudentBac: userProfile?.physicalDocStudentBac ?? false,
          physicalDocStudentReleveBac: userProfile?.physicalDocStudentReleveBac ?? false,
          physicalDocStudentDernierDiplome: userProfile?.physicalDocStudentDernierDiplome ?? false,
          physicalDocStudentReleveDiplome: userProfile?.physicalDocStudentReleveDiplome ?? false,
          physicalDocStudentReleveAnneesPrecedentes: userProfile?.physicalDocStudentReleveAnneesPrecedentes ?? false,
          physicalDocStudentCv: userProfile?.physicalDocStudentCv ?? false,
          physicalDocStudentLettreMotivation: userProfile?.physicalDocStudentLettreMotivation ?? false,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof StudentProfileData, value: string | number | boolean | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // A filière transfer is an official act: without the PV, E-DEP would be
      // missing the document that justifies the student's new file.
      if (requiresFiliereChangePv && !pvChangementDate) {
        setError('Renseignez la date du PV de changement de filière avant d\'enregistrer.');
        setSaving(false);
        return;
      }
      if (optionTooEarlyMessage) {
        setError(optionTooEarlyMessage);
        setSaving(false);
        return;
      }

      // Combine formData with academic fields stored in separate state
      const dataToSave = {
        ...omitEmptyStrings(formData),
        // Include academic enrollment fields
        codeFiliereInscrit: codeFiliereInscrit || undefined,
        niveauFormation: niveauFormation || undefined,
        situation: situation || undefined,
        premiereAnneeInscription: premiereAnneeInscription || undefined,
        ...(requiresFiliereChangePv
          ? {
              pvChangementFiliere: {
                date: pvChangementDate,
                motif: pvChangementMotif.trim() || undefined,
              },
            }
          : {}),
      };

      const result = await adminUpdateStudentProfile(studentUserId, dataToSave);
      setSuccess(true);
      const change = result?.emailChange;
      if (change?.changed) {
        setEmailNotice(
          change.verificationSent
            ? `Email de connexion modifié : un lien de vérification a été envoyé à ${change.email}.`
            : `Email de connexion modifié en ${change.email}. L'envoi du lien de vérification a échoué : utilisez "Renvoyer" depuis la liste.`,
        );
      }
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, change?.changed ? 3500 : 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setProfile(null);
    setDocuments([]);
    setError(null);
    setSuccess(false);
    setPreviewDoc(null);
    onClose();
  };

  // Document handling
  const getDocumentByType = (type: StudentDocumentType): StudentDocument | undefined => {
    return documents.find(d => d.documentType === type);
  };

  const handleUploadClick = (docType: StudentDocumentType) => {
    setSelectedDocType(docType);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedDocType) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Type de fichier non supporté. Utilisez JPG, PNG, WebP ou PDF.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Le fichier est trop volumineux. Taille maximum: 10 Mo.');
      return;
    }

    setUploadingDoc(selectedDocType);
    setError(null);

    try {
      const uploadedDoc = await adminUploadStudentDocument(studentUserId, selectedDocType, file);
      // Update documents list
      setDocuments(prev => {
        const filtered = prev.filter(d => d.documentType !== selectedDocType);
        return [...filtered, uploadedDoc];
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du téléchargement');
    } finally {
      setUploadingDoc(null);
      setSelectedDocType(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocumentClick = (doc: StudentDocument) => {
    setDocToDelete(doc);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!docToDelete) return;

    setDeletingDocId(docToDelete.id);
    setError(null);

    try {
      await adminDeleteStudentDocument(studentUserId, docToDelete.id);
      setDocuments(prev => prev.filter(d => d.id !== docToDelete.id));
      setDeleteModalOpen(false);
      setDocToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setDocToDelete(null);
  };

  const getFileIcon = (mimeType?: string) => {
    if (mimeType?.startsWith('image/')) {
      return <Image className="w-5 h-5 text-blue-500" />;
    }
    return <File className="w-5 h-5 text-red-500" />;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={handleFileSelect}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden z-10">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-primary-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <User className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Modifier le profil étudiant
                </h2>
                <p className="text-sm text-gray-500">
                  {studentName}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary-600 animate-spin mb-4" />
                <p className="text-gray-500">Chargement du profil...</p>
              </div>
            ) : error && !profile ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-700">{error}</p>
                  <button
                    onClick={loadData}
                    className="text-sm text-red-600 hover:text-red-800 underline mt-2"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Error Alert */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Success Alert */}
                {success && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-700">
                      <p>Profil mis à jour avec succès!</p>
                      {emailNotice && <p className="mt-1 text-green-800 font-medium">{emailNotice}</p>}
                    </div>
                  </div>
                )}

                {/* Section 1: Identité */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <User className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 1: Identité</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                      <input
                        type="text"
                        value={formData.nom || ''}
                        onChange={(e) => handleInputChange('nom', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                      <input
                        type="text"
                        value={formData.prenom || ''}
                        onChange={(e) => handleInputChange('prenom', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom (Arabe)</label>
                      <ArabicInput
                        value={formData.nomArabe || ''}
                        onChange={(value) => handleInputChange('nomArabe', value)}
                        placeholder="الاسم العائلي"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prénom (Arabe)</label>
                      <ArabicInput
                        value={formData.prenomArabe || ''}
                        onChange={(value) => handleInputChange('prenomArabe', value)}
                        placeholder="الاسم الشخصي"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CNIE</label>
                      <input
                        type="text"
                        value={formData.cnie || ''}
                        onChange={(e) => handleInputChange('cnie', e.target.value)}
                        placeholder="Ex: AB123456"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Passeport</label>
                      <input
                        type="text"
                        value={formData.passport || ''}
                        onChange={(e) => handleInputChange('passport', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sexe</label>
                      <select
                        value={formData.sexe || ''}
                        onChange={(e) => handleInputChange('sexe', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {genderOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                      <input
                        type="date"
                        value={formData.dateNaissance || ''}
                        onChange={(e) => handleInputChange('dateNaissance', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pays d'origine</label>
                      <select
                        value={formData.paysOrigine || ''}
                        onChange={(e) => handleInputChange('paysOrigine', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {countries.map(c => (
                          <option key={c.code} value={c.code}>{c.nameFr}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Situation de handicap</label>
                      <select
                        value={formData.handicap || ''}
                        onChange={(e) => handleInputChange('handicap', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {handicapOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 2: Coordonnées */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 2: Coordonnées</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                      <div className="flex">
                        <PhoneCountrySelector
                          value={phoneCountry}
                          onChange={(code) => {
                            setPhoneCountry(code);
                            handleInputChange('phoneCountry', code);
                          }}
                        />
                        <input
                          type="tel"
                          value={formData.telephone || ''}
                          onChange={(e) => handleInputChange('telephone', e.target.value)}
                          placeholder="6XX XXX XXX"
                          className="flex-1 px-3 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2 lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                      <input
                        type="text"
                        value={formData.adresse || ''}
                        onChange={(e) => handleInputChange('adresse', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Contact d'Urgence */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 3: Contact d'Urgence</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom du contact</label>
                      <input
                        type="text"
                        value={formData.urgenceNom || ''}
                        onChange={(e) => handleInputChange('urgenceNom', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Relation</label>
                      <select
                        value={formData.urgenceRelation || ''}
                        onChange={(e) => handleInputChange('urgenceRelation', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        {emergencyRelationSelectOptions().map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                      <div className="flex">
                        <PhoneCountrySelector
                          value={urgencePhoneCountry}
                          onChange={(code) => {
                            setUrgencePhoneCountry(code);
                            handleInputChange('urgencePhoneCountry', code);
                          }}
                        />
                        <input
                          type="tel"
                          value={formData.urgenceNumero || ''}
                          onChange={(e) => handleInputChange('urgenceNumero', e.target.value)}
                          placeholder="6XX XXX XXX"
                          className="flex-1 px-3 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2 lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                      <input
                        type="text"
                        value={formData.urgenceAdresse || ''}
                        onChange={(e) => handleInputChange('urgenceAdresse', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 4: Académique */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 4: Académique</h3>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">N° Massar/CNE</label>
                        <input
                          type="text"
                          value={formData.numeroMassarCNE || ''}
                          onChange={(e) => handleInputChange('numeroMassarCNE', e.target.value)}
                          placeholder="R123456789 ou 'be'"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">'be' pour les étudiants ayant obtenu un baccalauréat non marocain</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Filière</label>
                        <FilterableDropdown
                          options={filieres.map((f) => {
                            const branch = f.parentCodeFiliere ? optionBranchYear(f.parentCodeFiliere) : null;
                            return {
                              value: f.codeFiliere,
                              label: f.filiere,
                              sublabel: branch
                                ? `Option à partir de la ${branch}ème année - ${f.diplome}`
                                : `${f.diplome} - ${f.nombreAnnees} ans`,
                            };
                          })}
                          value={codeFiliereInscrit}
                          onChange={(value: string) => {
                            setCodeFiliereInscrit(value);
                            setNiveauFormation(1);
                          }}
                          placeholder="Sélectionner une filière..."
                        />
                        {optionTooEarlyMessage && (
                          <p className="text-xs text-red-600 mt-1">{optionTooEarlyMessage}</p>
                        )}
                      </div>
                    </div>

                    {/* Back to the filière the student started the year in */}
                    {isReturnToOrigin && (
                      <div className="p-4 border border-blue-300 bg-blue-50 rounded-lg">
                        <p className="text-sm font-semibold text-blue-900">
                          Retour à la filière de début d'année
                        </p>
                        <p className="text-xs text-blue-800 mt-1">
                          L'étudiant revient dans{' '}
                          {filieres.find((f) => f.codeFiliere === codeFiliereInscrit)?.filiere ||
                            codeFiliereInscrit}
                          , la filière où il était en début d'année. Pour E-DEP, aucun changement
                          n'a eu lieu :{' '}
                          <strong>
                            le PV de changement enregistré et son document seront supprimés
                          </strong>{' '}
                          et aucun nouveau PV n'est requis. Il devra être affecté manuellement à un
                          groupe.
                        </p>
                      </div>
                    )}

                    {/* Filière transfer: the PV is mandatory and goes to E-DEP */}
                    {requiresFiliereChangePv && (
                      <div className="p-4 border border-amber-300 bg-amber-50 rounded-lg space-y-3">
                        <div>
                          <p className="text-sm font-semibold text-amber-900">
                            PV de changement de filière requis
                          </p>
                          <p className="text-xs text-amber-800 mt-1">
                            {previousFiliere?.filiere || originalCodeFiliere} →{' '}
                            {selectedFiliere?.filiere || codeFiliereInscrit}. Ce PV accompagne le
                            dossier de l'étudiant dans E-DEP.
                          </p>
                          <p className="text-xs text-amber-800 mt-1">
                            Le changement prend effet sur l'année d'inscription en cours de
                            l'étudiant — celle de la rentrée à venir une fois le jury passé.
                            L'étudiant perdra son groupe de cette année-là et devra être affecté
                            manuellement dans la nouvelle filière ; ses groupes, notes et PV des
                            années déjà terminées sont conservés.
                          </p>
                        </div>

                        {replacesExistingTransfer && currentYearChange && (
                          <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2">
                            <strong>Un changement est déjà enregistré cette année</strong> (
                            {currentYearChange.fromFiliereName || currentYearChange.fromCodeFiliere}{' '}
                            → {currentYearChange.toFiliereName || currentYearChange.toCodeFiliere}).
                            Il sera remplacé : le PV précédent et son document seront supprimés, et
                            ce nouveau PV devra être téléversé.
                          </p>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Date du PV <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={pvChangementDate}
                              onChange={(e) => setPvChangementDate(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Motif (optionnel)
                            </label>
                            <input
                              type="text"
                              value={pvChangementMotif}
                              onChange={(e) => setPvChangementMotif(e.target.value)}
                              placeholder="Réorientation à la demande de l'étudiant"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-amber-900 bg-amber-100 border border-amber-200 rounded px-3 py-2">
                          <strong>Après l'enregistrement :</strong> téléversez le PV signé dans
                          l'onglet <strong>Documents</strong> de cette fiche, rubrique « PV de
                          changement de filière ». Il apparaîtra dans le dossier E-DEP de
                          l'étudiant. Ce document reste réservé à l'administration — l'étudiant ne
                          le voit jamais.
                        </p>
                      </div>
                    )}

                    {selectedFiliere && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Diplôme:</strong> {selectedFiliere.diplome} | <strong>Durée:</strong> {selectedFiliere.nombreAnnees} ans
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Niveau d'inscription</label>
                        <select
                          value={niveauFormation}
                          onChange={(e) => setNiveauFormation(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          disabled={!codeFiliereInscrit}
                        >
                          {Array.from({ length: maxNiveau }, (_, i) => i + 1).map((level) => (
                            <option key={level} value={level}>
                              {level === 1 ? '1ère année' : `${level}ème année`}
                            </option>
                          ))}
                        </select>
                        {selectedFiliere && (
                          <p className="text-xs text-gray-500 mt-1">Programme de {selectedFiliere.nombreAnnees} ans</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Situation</label>
                        <select
                          value={situation}
                          onChange={(e) => setSituation(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          {SITUATION_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">1ère année d'inscription</label>
                        <select
                          value={premiereAnneeInscription}
                          onChange={(e) => setPremiereAnneeInscription(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">-- Sélectionner --</option>
                          {anneesUniversitaires.map((a) => (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 5: Diplôme d'Accès */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <Award className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 5: Diplôme d'Accès</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Série du Baccalauréat</label>
                      <select
                        value={formData.serieBaccalaureat || ''}
                        onChange={(e) => handleInputChange('serieBaccalaureat', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {seriesBac.map(s => (
                          <option key={s.code} value={s.code}>{s.labelFr}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Année d'obtention du Bac</label>
                      <select
                        value={formData.anneeObtentionBac || ''}
                        onChange={(e) => handleInputChange('anneeObtentionBac', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {anneesUniversitaires.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Diplôme d'accès</label>
                      <select
                        value={formData.diplomeAcces || ''}
                        onChange={(e) => handleInputChange('diplomeAcces', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {diplomes.map(d => (
                          <option key={d.id} value={d.labelFr}>{d.labelFr}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spécialité du diplôme</label>
                      <input
                        type="text"
                        value={formData.specialiteDiplomeAcces || ''}
                        onChange={(e) => handleInputChange('specialiteDiplomeAcces', e.target.value)}
                        placeholder="Ex: Sciences Économiques"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mention</label>
                      <select
                        value={formData.mentionDiplomeAcces || ''}
                        onChange={(e) => handleInputChange('mentionDiplomeAcces', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {mentionOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Lieu d'obtention</label>
                      <select
                        value={formData.lieuObtentionDiplomeAcces || ''}
                        onChange={(e) => handleInputChange('lieuObtentionDiplomeAcces', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {provinces.map(p => (
                          <option key={p.id} value={p.nameFr}>{p.nameFr}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 6: Statut & Aides */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 6: Statut & Aides</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Résident</label>
                      <select
                        value={formData.resident || ''}
                        onChange={(e) => handleInputChange('resident', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {residentOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fonctionnaire</label>
                      <select
                        value={formData.fonctionnaire || ''}
                        onChange={(e) => handleInputChange('fonctionnaire', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {fonctionnaireOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pourcentage de bourse</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={formData.pourcentageBourse ?? ''}
                        onChange={(e) => handleInputChange('pourcentageBourse', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="0-100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mobilité</label>
                      <select
                        value={formData.mobilite || ''}
                        onChange={(e) => handleInputChange('mobilite', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {mobiliteOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section: Frais Financiers (aperçu — gestion dans Gestion des Paiements) */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <StudentFinancialSection
                    studentId={profile?.student?.id}
                    studentCode={profile?.student?.studentCode}
                  />
                </div>

                {/* Section 7: Documents */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 7: Documents</h3>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {visibleDocumentTypes.map(docType => {
                        const existingDoc = getDocumentByType(docType.type);
                        const isUploading = uploadingDoc === docType.type;
                        const isDeleting = deletingDocId === existingDoc?.id;

                        return (
                          <div
                            key={docType.type}
                            className={`border rounded-lg p-4 ${existingDoc ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-900 text-sm">{docType.label}</h4>
                                <p className="text-xs text-gray-500 mt-0.5">{docType.description}</p>
                              </div>
                            </div>

                            {existingDoc ? (
                              <div className="mt-3">
                                <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                                  {getFileIcon(existingDoc.mimeType)}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-700 truncate">{existingDoc.fileName}</p>
                                    <p className="text-xs text-gray-500">{formatFileSize(existingDoc.fileSize)}</p>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <a
                                      href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${existingDoc.fileUrl}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded transition-colors"
                                      title="Voir"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </a>
                                    <button
                                      onClick={() => handleDeleteDocumentClick(existingDoc)}
                                      disabled={isDeleting}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                      title="Supprimer"
                                    >
                                      {isDeleting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Trash2 className="w-4 h-4" />
                                      )}
                                    </button>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleUploadClick(docType.type)}
                                  disabled={isUploading}
                                  className="mt-2 w-full px-3 py-1.5 text-xs text-primary-600 border border-primary-300 rounded hover:bg-primary-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                                >
                                  {isUploading ? (
                                    <>
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                      Téléchargement...
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-3 h-3" />
                                      Remplacer
                                    </>
                                  )}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleUploadClick(docType.type)}
                                disabled={isUploading}
                                className="mt-3 w-full px-3 py-2 text-sm text-gray-600 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {isUploading ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Téléchargement...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4" />
                                    Télécharger
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-4">
                      Formats acceptés: JPG, PNG, WebP, PDF. Taille maximum: 10 Mo par fichier.
                    </p>
                  </div>
                </div>

                {/* Section 8: Documents physiques (originaux reçus) */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-primary-600" />
                    <h3 className="font-medium text-gray-900">Section 8: Documents physiques (originaux reçus)</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {physicalDocFields.filter((doc) => {
                      // Only show conditional documents when they actually apply to this
                      // student — mirrors the Suivi des documents physiques modal.
                      if (doc.condition === 'nonBac') return !isBacDiplomeAcces(formData.diplomeAcces as string | undefined);
                      if (doc.condition === 'transfer') return (profile?.student?.niveauFormationInitial ?? 1) > 1;
                      return true;
                    }).map((doc) => (
                      <label
                        key={doc.field}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          formData[doc.field] ? 'border-green-200 bg-green-50 hover:bg-green-100' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData[doc.field] ?? false}
                          onChange={(e) => handleInputChange(doc.field, e.target.checked)}
                          className="w-5 h-5 mt-0.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <span className={`text-sm font-medium ${formData[doc.field] ? 'text-green-900' : 'text-gray-900'}`}>
                            {doc.label}
                          </span>
                          {doc.hint && (
                            <p className="text-xs text-gray-500 mt-0.5">{doc.hint}</p>
                          )}
                        </div>
                        {formData[doc.field] && <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              disabled={saving}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || success}
              className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enregistrement...
                </>
              ) : success ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Enregistré!
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Enregistrer les modifications
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70">
          <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden">
            <button
              onClick={() => setPreviewDoc(null)}
              className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
            >
              <X className="w-5 h-5" />
            </button>
            {previewDoc.mimeType?.startsWith('image/') ? (
              <img
                src={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${previewDoc.fileUrl}`}
                alt={previewDoc.fileName}
                className="max-h-[85vh] object-contain"
              />
            ) : (
              <iframe
                src={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${previewDoc.fileUrl}`}
                className="w-full h-[85vh]"
                title={previewDoc.fileName}
              />
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        documentName={docToDelete?.fileName || ''}
        isDeleting={!!deletingDocId}
      />
    </div>
  );
};

export default DirectEditStudentProfileModal;
